import { FormGroup } from '@angular/forms';
import { FilingFormState, FilingFormStateService } from '../services/mbrs/filing-form-state.service';
import { revealValidationErrors } from './validation-reveal.util';
import type { LoadTrace } from '../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from './sheet-load-trace.util';

/**
 * Deterministic replacement for the legacy setTimeout(100→600) sheet-init
 * finalization.
 *
 * TIER-A (today): omit `awaitSettled`. Safe when init mutations reach one of
 * these two routes:
 *
 *   1. Every init mutation uses `emitEvent: false`, so the form-edit pipeline
 *      never fires during init — nothing to wait on, finalize runs straight
 *      through.
 *   2. Each rule write is paired with a synchronous `syncFormToState()`, so
 *      the value is already in `modifiedStateSubject` before finalize runs,
 *      regardless of any debounced pipeline that fires later. Role0 and role1
 *      use this route: their reactive-rule appliers call `syncFormToState()`
 *      unconditionally after each write.
 *
 * TIER-B (future): a calc-heavy sheet passes `awaitSettled` — a closure that
 * resolves when its debounced calc/persist pipeline has settled (once the
 * pipeline exposes a deterministic "settled" signal).
 * The helper waits for it before snapshotting the baseline, replacing the 600ms
 * guess. Adoption is additive — no other call-site change.
 */
export async function finalizeSheetInit(
  opts: {
    stateService: FilingFormStateService;
    sheetKey: keyof FilingFormState;
    form: FormGroup;
    setLoadingState: (isLoading: boolean) => void;
    persistInitial?: () => void;
    awaitSettled?: () => Promise<void>;
    /**
     * Answers "is this control computed rather than preparer-entered?" — pass a
     * predicate over the sheet's own schema (`ui.calculated || ui.readonly`) so
     * an empty total is not reported as a skipped mandatory field. Forwarded
     * verbatim to {@link revealValidationErrors}; omit it and every control is
     * treated as preparer-entered.
     */
    isComputed?: (controlName: string) => boolean;
    /**
     * The calling sheet's load trace, so the two steps this helper actually
     * owns get marked at the point they happen.
     *
     * It matters that they are marked HERE rather than in the sheet: the
     * `waitForMbrsHydration()` await below is the real step 1 for every sheet
     * that finalizes through this helper, and it is the await that can hang
     * forever. Marking step 1 in a sheet that merely *mentions* the wait in a
     * comment would report a step that never happened, which is the one way a
     * diagnostic code can actively mislead.
     */
    trace?: LoadTrace;
  }
): Promise<void> {
  // The sheet key doubles as the stall-tripwire label, so a sheet that hangs
  // here names itself in the warning. See waitForMbrsHydration's docblock.
  await opts.stateService.waitForMbrsHydration(opts.sheetKey);
  opts.trace?.mark(SHEET_LOAD_STEPS.hydrationSettled);
  await Promise.resolve();

  opts.setLoadingState(false);
  opts.persistInitial?.();

  await opts.awaitSettled?.();

  // Reveal the sheet's already-computed errors now that the form exists and has
  // been populated. This is the deterministic replacement for the filing-form
  // container's `setTimeout`-driven `markAllAsTouched`, which silently no-ops
  // whenever a sheet's async init has not yet assigned its FormGroup — see
  // revealValidationErrors' docblock. Touch-only, so it cannot move the
  // `!form.valid` verdict published on the next line.
  revealValidationErrors(opts.form, { isComputed: opts.isComputed });

  opts.stateService.markSheetInitializedWithStatus(opts.sheetKey, !opts.form.valid);

  // Terminal step. Marking it closes the trace as a success and cancels the
  // stall watchdog.
  opts.trace?.mark(SHEET_LOAD_STEPS.finalized);
}
